<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.canteen'),
                        href: null,
                    },
                    {
                        name: meta.date_display,
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <GroupSelector
                                :grade-levels="meta.links.gradeLevels"
                                :user-grade-levels="meta.user_grade_levels"
                            >
                                <template v-slot:default="{ filteredGradeLevels }">
                                    <div class="mb-2" v-for="gradeLevel in filteredGradeLevels" :key="gradeLevel.id">
                                        <AppButton
                                            element="inertia-link"
                                            v-for="group in gradeLevel.groups"
                                            :href="group.url"
                                            :key="group.id"
                                            desired-action
                                            class="mr-2"
                                        >
                                            {{ group.name }}
                                        </AppButton>
                                    </div>
                                </template>
                            </GroupSelector>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter" id="canteen_day">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.groups', 2)" for="active" />
                        <SlideOverCheckboxInput v-model="form.group_ids" :options="options.groups">
                            <div class="flex justify-end">
                                <div class="mt-1">
                                    <SubmitFormButton
                                        :button-text="$t('messages.filter')"
                                        :processing="form.processing"
                                        is-filter
                                    />
                                </div>
                            </div>
                        </SlideOverCheckboxInput>
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label for="food_allergy_diet" class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.food_allergy') }} /
                        {{ $t('messages.diet') }}
                    </label>
                    <div class="mt-1">
                        <input
                            id="food_allergy_diet"
                            v-model="form.food_allergy_diet"
                            name="food_allergy_diet"
                            type="checkbox"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <label for="food_allergy_diet" class="block text-sm font-medium leading-5 text-gray-700">
                        {{ $t('messages.show_only_those_who_attended') }}
                    </label>
                    <div class="mt-1">
                        <input
                            id="attendance_today"
                            v-model="form.attendance_today"
                            name="attendance_today"
                            type="checkbox"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date')" for="date" />
                        <FormInput id="date" v-model="form.date" name="date" type="date" class="mt-1 w-full" />
                        <FormError :value="form.errors.date" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.group') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="hidden max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.canteen') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.absent') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.justified') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.allergy') }} /
                                    {{ $t('messages.diet') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.comment') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in daysForm.students"
                                :key="student.id"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ student.group_name }}
                                </td>
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <div class="flex items-center space-x-1">
                                        <a :href="student.links.show">
                                            {{ student.full_name }}
                                        </a>

                                        <BadgePill color="indigo" v-if="student.is_absent">
                                            {{ $t('messages.absent') }}
                                        </BadgePill>
                                    </div>
                                </td>
                                <td class="hidden px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex space-x-1">
                                        <BadgePill
                                            v-for="day in student.days"
                                            :color="day.value ? 'indigo' : 'gray'"
                                            :key="day.text"
                                        >
                                            {{ $t(`messages.${day.text}`) }}
                                        </BadgePill>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <input
                                        type="checkbox"
                                        v-model="student.canteenDay.is_absent"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <input
                                        v-if="student.canteenDay.is_absent"
                                        type="checkbox"
                                        v-model="student.canteenDay.is_justified"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.tags }}
                                </td>
                                <td class="px-6 py-4 text-sm leading-5 text-gray-500">
                                    {{ student.lunch_comment }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <form action="" @submit.prevent="updateDays()" v-if="meta.can.create">
            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="daysForm.processing">
                    {{ $t('messages.save') }}
                    <span v-if="daysForm.processing" class="ml-1 btn-spinner" />
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import BadgePill from '@/components/BadgePill.vue';
import GroupSelector from '@/components/GroupSelector.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        AppButton,
        FormLabel,
        SlideOverCheckboxInput,
        BadgePill,
        FormError,
        ActivityMenuOptions,
        GroupSelector,
        FormInput,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'students', 'filters', 'options', 'students_count', 'abcenses'],
    data() {
        return {
            form: this.$inertia.form({
                group_ids: this.filters.group_ids,
                dayOfWeek: this.filters.dayOfWeek,
                date: this.filters.date,
                food_allergy_diet: this.filters.food_allergy_diet,
                attendance_today: this.filters.attendance_today,
            }),
            daysForm: this.$inertia.form({
                students: this.students,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        updateDays() {
            this.daysForm
                .transform((data) => {
                    const canteenDays = data.students.map((s) => s.canteenDay);
                    return {
                        canteenDays: canteenDays,
                    };
                })
                .patch(this.meta.links.toggle_days, {
                    onSuccess: () => {},
                });
        },
    },
};
</script>
